import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useLanguage } from '../../hooks/useLanguage';
import { isAuthenticated } from '../../utils/tokenManager';
import { AnimatedBackground } from '../ui/AnimatedBackground';

// import { Footer } from './Footer';
import { Header } from './Header';
import { Sidebar } from './Sidebar';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true); // Default open on desktop
  const [isDesktop, setIsDesktop] = useState(false);
  const { isRTL } = useLanguage();
  const location = useLocation();
  const auth = isAuthenticated();

  // Initialize and handle screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      
      // On mobile, always close sidebar initially
      if (!desktop) {
        setIsSidebarOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Close sidebar on route change (mobile/tablet only - NOT desktop)
  useEffect(() => {
    if (!isDesktop) {
      setIsSidebarOpen(false);
    }
    // On desktop, keep sidebar state - don't close on route change
  }, [location.pathname, isDesktop]);

  const handleMenuToggle = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Animated Background */}
      <AnimatedBackground />
      
      <Header
        onMenuToggle={auth ? handleMenuToggle : undefined}
        isMenuOpen={isSidebarOpen}
      />

      <div className="flex flex-1 relative z-10">
        {/* Sidebar - only show if authenticated */}
        {auth && <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />}

        {/* Main Content */}
        <main
          className={`flex-1 transition-all duration-300 relative z-10 ${
            auth && isSidebarOpen ? (isRTL ? 'lg:mr-64' : 'lg:ml-64') : ''
          }`}
          role="main"
        >
          <div className={`${auth && isSidebarOpen ? 'max-w-7xl' : 'max-w-6xl'} mx-auto px-4 sm:px-6 py-6 md:py-10`}>
            {children}
          </div>
        </main>
      </div>

      {/* Footer - only show for non-authenticated users
      {!auth && (
        <div className="relative z-10">
          <Footer />
        </div>
      )} */}
    </div>
  );
}

